/**
 * Tasks Settings Tab
 *
 * Epic 10: Manage tasks with CRUD operations, toggle active status,
 * and delete protection for tasks with employee assignments.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tasksApi, type TaskListItem, type CreateTaskInput, type UpdateTaskInput } from '../../../lib/tasksApi';
import { competenciesApi, type Competency } from '../../../lib/competenciesApi';
import { TASK_WEGING_MIN, TASK_WEGING_MAX } from '@functieloonhuis/shared';
import { matchesSearch } from '../../../lib/textUtils';

type StatusFilter = 'all' | 'active' | 'inactive';

interface TaskFormData {
  name: string;
  beschrijving: string;
  /**
   * Leeg tijdens het typen (wissen-om-opnieuw-te-typen moet kunnen). Het
   * `required`-attribuut op het veld blokkeert opslaan zolang het leeg is,
   * dus `handleSubmit` ziet altijd een getal.
   */
  weging: number | '';
  competencyIds: number[];
}

interface DeleteError {
  code: string;
  error: string;
  employeeCount?: number;
}

export function TasksSettingsTab() {
  const queryClient = useQueryClient();

  // Data fetching
  const { data: tasksData, isLoading, error } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => tasksApi.list(),
  });

  // Fetch vak competencies for linking
  const { data: vakCompetencies } = useQuery<Competency[]>({
    queryKey: ['competencies', 'vak'],
    queryFn: () => competenciesApi.getVak(false), // Include inactive for editing
  });

  // Mutations
  const createTask = useMutation({
    mutationFn: (data: CreateTaskInput) => tasksApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  const updateTask = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateTaskInput }) => tasksApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  const toggleActive = useMutation({
    mutationFn: (id: number) => tasksApi.toggleActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  const deleteTask = useMutation({
    mutationFn: (id: number) => tasksApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setDeleteModalState({ isOpen: false, task: null, error: null });
    },
    onError: (error: DeleteError) => {
      setDeleteModalState(prev => ({ ...prev, error }));
    },
  });

  const linkCompetencies = useMutation({
    mutationFn: ({ taskId, competencyIds }: { taskId: number; competencyIds: number[] }) =>
      competenciesApi.linkToTask(taskId, competencyIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['competencies'] });
    },
  });

  // Local state
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskListItem | null>(null);
  const [formData, setFormData] = useState<TaskFormData>({
    name: '',
    beschrijving: '',
    weging: 1,
    competencyIds: [],
  });
  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean;
    task: TaskListItem | null;
    error: DeleteError | null;
  }>({ isOpen: false, task: null, error: null });

  // Filter tasks (accent-insensitive)
  const filteredTasks = tasksData?.data.filter(task => {
    const matchesStatusFilter =
      statusFilter === 'all' ||
      (statusFilter === 'active' && task.isActief) ||
      (statusFilter === 'inactive' && !task.isActief);
    const matchesSearchFilter = matchesSearch(task.name, searchQuery);
    return matchesStatusFilter && matchesSearchFilter;
  }) ?? [];

  // Form handlers
  const resetForm = () => {
    setFormData({ name: '', beschrijving: '', weging: 1, competencyIds: [] });
    setEditingTask(null);
    setIsFormOpen(false);
  };

  const handleEdit = (task: TaskListItem) => {
    setEditingTask(task);
    setFormData({
      name: task.name,
      beschrijving: task.beschrijving || '',
      weging: task.weging,
      competencyIds: task.competencies?.map(c => c.id) || [],
    });
    setIsFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Het `required`-attribuut vangt dit in de browser al af; deze guard is voor
    // het geval het formulier programmatisch wordt ingediend.
    if (formData.weging === '') return;
    const weging = formData.weging;

    if (editingTask) {
      await updateTask.mutateAsync({
        id: editingTask.id,
        data: {
          name: formData.name,
          beschrijving: formData.beschrijving || undefined,
          weging,
        },
      });
      // Update competency links
      await linkCompetencies.mutateAsync({
        taskId: editingTask.id,
        competencyIds: formData.competencyIds,
      });
    } else {
      const newTask = await createTask.mutateAsync({
        name: formData.name,
        beschrijving: formData.beschrijving || undefined,
        weging,
      });
      // Link competencies to new task
      if (formData.competencyIds.length > 0) {
        await linkCompetencies.mutateAsync({
          taskId: newTask.id,
          competencyIds: formData.competencyIds,
        });
      }
    }
    resetForm();
  };

  const handleDeleteClick = (task: TaskListItem) => {
    setDeleteModalState({ isOpen: true, task, error: null });
  };

  const handleConfirmDelete = async () => {
    if (deleteModalState.task) {
      try {
        await deleteTask.mutateAsync(deleteModalState.task.id);
      } catch {
        // Error handled in onError callback
      }
    }
  };

  const handleToggleAndCloseModal = async () => {
    if (deleteModalState.task) {
      await toggleActive.mutateAsync(deleteModalState.task.id);
      setDeleteModalState({ isOpen: false, task: null, error: null });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Laden...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-700">Fout bij het laden van taken.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Taken</h2>
          <p className="text-sm text-gray-500">Beheer taken met wegingen voor niveau-bepaling</p>
        </div>
        <button
          onClick={() => setIsFormOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          <svg className="mr-2 -ml-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Nieuwe Taak
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-4">
        <div>
          <label htmlFor="status-filter" className="sr-only">Status filter</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="all">Alle taken</option>
            <option value="active">Actief</option>
            <option value="inactive">Inactief</option>
          </select>
        </div>
        <div className="flex-1">
          <label htmlFor="search" className="sr-only">Zoeken</label>
          <input
            type="text"
            id="search"
            placeholder="Zoeken op naam..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
        </div>
      </div>

      {/* Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500/75 transition-opacity" onClick={resetForm}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="relative z-10 inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <form onSubmit={handleSubmit}>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingTask ? 'Taak Bewerken' : 'Nieuwe Taak'}
                </h3>

                <div className="space-y-4">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Naam *
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                      maxLength={200}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label htmlFor="beschrijving" className="block text-sm font-medium text-gray-700">
                      Beschrijving
                    </label>
                    <textarea
                      id="beschrijving"
                      rows={3}
                      value={formData.beschrijving}
                      onChange={(e) => setFormData({ ...formData, beschrijving: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      maxLength={1000}
                    />
                  </div>

                  {/* Weging */}
                  <div>
                    <label htmlFor="weging" className="block text-sm font-medium text-gray-700">
                      Weging (punten) *
                    </label>
                    <input
                      type="number"
                      id="weging"
                      min={TASK_WEGING_MIN}
                      max={TASK_WEGING_MAX}
                      step={1}
                      value={formData.weging}
                      onChange={(e) => {
                        const raw = e.target.value;
                        // Leeg mag: anders kan de gebruiker het veld niet wissen om
                        // opnieuw te typen, en gaat `required` nooit af.
                        if (raw === '') {
                          setFormData({ ...formData, weging: '' });
                          return;
                        }
                        // `type="number"` laat ook '30.7' en '1e3' door. `parseInt`
                        // zou die stil afkappen tot 30 en 1; liever de vorige waarde
                        // aanhouden dan een getal invoeren dat niemand heeft getypt.
                        const parsed = Number(raw);
                        if (!Number.isInteger(parsed)) return;
                        setFormData({ ...formData, weging: parsed });
                      }}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Punten die bijdragen aan de niveau-bepaling van medewerkers ({TASK_WEGING_MIN} t/m {TASK_WEGING_MAX})
                    </p>
                  </div>

                  {/* Vakcompetenties */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Vakcompetenties
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Selecteer de competenties die bij deze taak horen
                    </p>
                    {vakCompetencies && vakCompetencies.filter(c => c.isActive).length > 0 ? (
                      <div className="mt-1 border border-gray-300 rounded-md max-h-40 overflow-y-auto">
                        {vakCompetencies.filter(c => c.isActive).map((comp) => (
                          <label
                            key={comp.id}
                            className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <input
                              type="checkbox"
                              checked={formData.competencyIds.includes(comp.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({
                                    ...formData,
                                    competencyIds: [...formData.competencyIds, comp.id],
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    competencyIds: formData.competencyIds.filter(id => id !== comp.id),
                                  });
                                }
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">{comp.name}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">
                        Geen vakcompetenties beschikbaar. Maak eerst vakcompetenties aan in de Competenties tab.
                      </p>
                    )}
                    {formData.competencyIds.length > 0 && (
                      <p className="mt-1 text-xs text-gray-500">
                        {formData.competencyIds.length} competentie(s) geselecteerd
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Annuleren
                  </button>
                  <button
                    type="submit"
                    disabled={createTask.isPending || updateTask.isPending}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createTask.isPending || updateTask.isPending ? 'Opslaan...' : 'Opslaan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalState.isOpen && deleteModalState.task && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="delete-modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500/75 transition-opacity"
              onClick={() => setDeleteModalState({ isOpen: false, task: null, error: null })}
            ></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="relative z-10 inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              {deleteModalState.error?.code === 'TASK_HAS_ASSIGNMENTS' ? (
                // Blocked: task has employees
                <>
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
                      <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <h3 className="ml-3 text-lg font-medium text-gray-900">
                      Taak verwijderen niet mogelijk
                    </h3>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">
                    De taak "{deleteModalState.task.name}" is toegewezen aan{' '}
                    <strong>{deleteModalState.task.employeesCount} medewerker(s)</strong> en kan niet worden verwijderd.
                  </p>
                  <p className="text-sm text-gray-500 mb-4">
                    Opties:
                  </p>
                  <ul className="text-sm text-gray-500 list-disc list-inside mb-4">
                    <li>Zet de taak op inactief</li>
                    <li>Verwijder eerst de koppelingen met medewerkers</li>
                  </ul>
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setDeleteModalState({ isOpen: false, task: null, error: null })}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Sluiten
                    </button>
                    <button
                      type="button"
                      onClick={handleToggleAndCloseModal}
                      disabled={toggleActive.isPending}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50"
                    >
                      {toggleActive.isPending ? 'Bezig...' : 'Inactief zetten'}
                    </button>
                  </div>
                </>
              ) : (
                // Normal delete confirmation
                <>
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                      <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </div>
                    <h3 className="ml-3 text-lg font-medium text-gray-900">
                      Taak verwijderen
                    </h3>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">
                    Weet u zeker dat u de taak "{deleteModalState.task.name}" wilt verwijderen?
                    Deze actie kan niet ongedaan worden gemaakt.
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setDeleteModalState({ isOpen: false, task: null, error: null })}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Annuleren
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmDelete}
                      disabled={deleteTask.isPending}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                    >
                      {deleteTask.isPending ? 'Verwijderen...' : 'Verwijderen'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tasks List */}
      {filteredTasks.length > 0 ? (
        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Taak
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Weging
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Medewerkers
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acties
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTasks.map((task) => (
                <tr key={task.id} className={`hover:bg-gray-50 ${!task.isActief ? 'opacity-60' : ''}`}>
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{task.name}</div>
                      {task.beschrijving && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">{task.beschrijving}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {task.weging} pt
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {task.employeesCount > 0 ? (
                      <span className="inline-flex items-center">
                        <svg className="mr-1 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        {task.employeesCount}
                      </span>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => toggleActive.mutate(task.id)}
                      disabled={toggleActive.isPending}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${
                        task.isActief ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {task.isActief ? 'Actief' : 'Inactief'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(task)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Bewerken
                    </button>
                    <button
                      onClick={() => handleDeleteClick(task)}
                      className="text-red-600 hover:text-red-900"
                      disabled={deleteTask.isPending}
                    >
                      Verwijderen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Geen taken gevonden</h3>
          <p className="mt-2 text-sm text-gray-500">
            {searchQuery || statusFilter !== 'all'
              ? 'Pas de filters aan of voeg een nieuwe taak toe.'
              : 'Begin met het toevoegen van taken.'}
          </p>
        </div>
      )}

      {/* Summary */}
      <div className="mt-4 text-sm text-gray-500">
        Totaal: {filteredTasks.length} taken
        {statusFilter !== 'all' && ` (gefilterd van ${tasksData?.total ?? 0})`}
      </div>
    </div>
  );
}
